/**
 * pipelineErrorMap — the single client-side interpreter for the terminal
 * close / reopen / terminal-add error surface (SE-5496, shared with SE-5498).
 *
 * The backend rejects an illegal terminal transition with a `GraphQLError`
 * carrying a machine-readable `extensions.code`. This module maps every code to
 * a user-facing message AND a HANDLING KIND, because two of the responses are
 * not "show an error":
 *
 * - `CARD_ALREADY_CLOSED` is NOT a hard error. The card is already in the exact
 *   state the user was trying to reach, so the caller should REFRESH the card
 *   and show its current closed state rather than surface a failure.
 * - A code-less `NotFoundException` (raised for a below-Manager caller who
 *   cannot see the card) means "no access / no such card". It must NOT be
 *   branched on as a real domain error — the caller silently declines the
 *   action instead of showing a scary message.
 *
 * Everything else is a toast. `TERMINAL_ENTRY_NOT_ALLOWED` (the add-into-a-
 * terminal-stage rejection from SE-5498) rides this same map so both paths
 * share one interpreter.
 * @module features/player-kanban/utils/pipelineErrorMap
 */

/** The machine-readable codes the backend attaches to a pipeline rejection. */
export enum PipelineErrorCode {
  /** The move claimed a terminal target but the stage is not terminal. */
  TerminalStageRequired = "TERMINAL_STAGE_REQUIRED",
  /** The chosen outcome is not in the board template's `validOutcomes`. */
  OutcomeNotAllowed = "OUTCOME_NOT_ALLOWED",
  /** A terminal close was attempted without the required reason. */
  ReasonRequired = "REASON_REQUIRED",
  /** The target stage belongs to a different pipeline than the card. */
  PipelineMismatch = "PIPELINE_MISMATCH",
  /** The reopen target is not a valid working (non-terminal) stage. */
  ReopenTargetInvalid = "REOPEN_TARGET_INVALID",
  /** Reopen was attempted on a card that is not closed. */
  CardNotClosed = "CARD_NOT_CLOSED",
  /** Close was attempted on a card already closed — handled as a refresh. */
  CardAlreadyClosed = "CARD_ALREADY_CLOSED",
  /** Add-into-a-terminal-stage rejection (SE-5498), shares this map. */
  TerminalEntryNotAllowed = "TERMINAL_ENTRY_NOT_ALLOWED",
}

/**
 * How the caller should react to a resolved pipeline error.
 * - `toast` — surface {@link PipelineErrorResolution.message} to the user.
 * - `refresh` — do NOT hard-error; refetch the card and show its current
 *   (already-closed) state.
 * - `silent` — a no-access / not-found signal; decline the action without a
 *   user-facing error.
 */
export type PipelineErrorKind = "toast" | "refresh" | "silent";

/** The resolved interpretation of a thrown pipeline error. */
export interface PipelineErrorResolution {
  /** The recognised code, or `null` when none was attached. */
  readonly code: PipelineErrorCode | null;
  /** What the caller should do about it. */
  readonly kind: PipelineErrorKind;
  /** The user-facing message (only meaningful for `kind === "toast"`). */
  readonly message: string;
}

/** Per-code user-facing copy for the toast path. */
const MESSAGES: Record<PipelineErrorCode, string> = {
  [PipelineErrorCode.TerminalStageRequired]:
    "That stage is not a terminal stage, so an outcome can't be recorded there.",
  [PipelineErrorCode.OutcomeNotAllowed]:
    "That outcome isn't available for this pipeline.",
  [PipelineErrorCode.ReasonRequired]:
    "A reason is required to close this card.",
  [PipelineErrorCode.PipelineMismatch]:
    "That stage belongs to a different pipeline.",
  [PipelineErrorCode.ReopenTargetInvalid]:
    "Pick a working stage to reopen this card into.",
  [PipelineErrorCode.CardNotClosed]: "This card isn't closed.",
  [PipelineErrorCode.CardAlreadyClosed]: "This card was already closed.",
  [PipelineErrorCode.TerminalEntryNotAllowed]:
    "Players can't be added straight into a terminal stage.",
};

/** Generic fallback when no known code is attached. */
export const PIPELINE_ERROR_FALLBACK =
  "Something went wrong. Please try again.";

/** The complete set of recognised codes, for membership checks. */
const KNOWN_CODES = new Set<string>(Object.values(PipelineErrorCode));

/** A single GraphQL error entry as it may appear on a thrown error. */
interface GraphQLErrorLike {
  readonly message?: string;
  readonly extensions?: { readonly code?: unknown } | null;
}

/**
 * Pull the candidate GraphQL error entries off an unknown thrown value,
 * covering both an ApolloError (`graphQLErrors`) and a bare GraphQLError
 * (`extensions` directly on the object).
 * @param error - The thrown value.
 * @returns Zero or more GraphQL-error-shaped entries.
 */
const graphQLErrorsOf = (error: unknown): GraphQLErrorLike[] => {
  if (typeof error !== "object" || error === null) return [];
  const record = error as {
    graphQLErrors?: unknown;
    extensions?: unknown;
    message?: unknown;
  };
  const fromApollo = Array.isArray(record.graphQLErrors)
    ? (record.graphQLErrors as GraphQLErrorLike[])
    : [];
  const bare: GraphQLErrorLike[] =
    record.extensions && typeof record.extensions === "object"
      ? [
          {
            extensions: record.extensions as { code?: unknown },
            message:
              typeof record.message === "string" ? record.message : undefined,
          },
        ]
      : [];
  return [...fromApollo, ...bare];
};

/**
 * Whether an entry looks like the code-less `NotFoundException` the backend
 * raises for a caller who cannot see the card. It carries NO
 * `extensions.code`, so it is recognised by its message/name text.
 * @param entry - A GraphQL error entry.
 * @returns `true` when the entry is a code-less not-found signal.
 */
const isCodelessNotFound = (entry: GraphQLErrorLike): boolean => {
  const code = entry.extensions?.code;
  if (typeof code === "string" && code.length > 0) return false;
  return /not\s*found/i.test(entry.message ?? "");
};

/**
 * Interpret a thrown pipeline error into a code + handling kind + message.
 *
 * Resolution order is deliberate: a recognised `extensions.code` wins over the
 * code-less not-found heuristic, so a real domain rejection is never masked as
 * "no access". An unrecognised error falls back to a generic toast.
 * @param error - The thrown value from a close/reopen/add mutation.
 * @returns The resolved interpretation (see {@link PipelineErrorResolution}).
 */
export const resolvePipelineError = (
  error: unknown
): PipelineErrorResolution => {
  const entries = graphQLErrorsOf(error);

  const matchedCode = entries
    .map(entry => entry.extensions?.code)
    .find(
      (code): code is string =>
        typeof code === "string" && KNOWN_CODES.has(code)
    );

  if (matchedCode) {
    const code = matchedCode as PipelineErrorCode;
    // CARD_ALREADY_CLOSED is a refresh, not a failure: the card is already in
    // the desired state, so reconcile and show it rather than error.
    if (code === PipelineErrorCode.CardAlreadyClosed) {
      return { code, kind: "refresh", message: MESSAGES[code] };
    }
    return { code, kind: "toast", message: MESSAGES[code] };
  }

  // A code-less NotFoundException = no access / no such card. Decline silently.
  if (entries.some(isCodelessNotFound)) {
    return { code: null, kind: "silent", message: "" };
  }

  return { code: null, kind: "toast", message: PIPELINE_ERROR_FALLBACK };
};
